import { PUBLIC_CONFIG } from "./lib/publicConfig";

const API_BASE_URL = PUBLIC_CONFIG.API_BASE_URL || "http://localhost:4000/api";
const DEFAULT_TIMEOUT_MS = 15000;
const UPLOAD_TIMEOUT_MS = 60000;
const API_BEARER_TOKEN = PUBLIC_CONFIG.API_BEARER_TOKEN;

function normalizeToken(token) {
  const normalized = String(token || "").trim();
  return normalized || null;
}

let currentAccessToken = normalizeToken(API_BEARER_TOKEN);
let tokenReader = null;
let tokenRefreshHandler = null;
let refreshInFlight = null;

export function setApiAccessToken(token) {
  currentAccessToken = normalizeToken(token);
}

export function clearApiSession() {
  currentAccessToken = normalizeToken(API_BEARER_TOKEN);
  refreshInFlight = null;
}

export function configureApiSession({ getAccessToken, refreshAccessToken } = {}) {
  tokenReader = typeof getAccessToken === "function" ? getAccessToken : null;
  tokenRefreshHandler = typeof refreshAccessToken === "function" ? refreshAccessToken : null;
}

function readAccessToken() {
  if (tokenReader) {
    const fromReader = normalizeToken(tokenReader());
    if (fromReader) return fromReader;
  }
  return currentAccessToken;
}

function identityHeaders() {
  const token = readAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function withTimeout(timeoutMs) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  return { signal: controller.signal, clear: () => clearTimeout(timeoutId) };
}

async function tryRefreshAccessToken() {
  if (!tokenRefreshHandler) return null;
  if (!refreshInFlight) {
    refreshInFlight = Promise.resolve(tokenRefreshHandler())
      .then((newToken) => {
        const normalized = normalizeToken(newToken);
        if (normalized) currentAccessToken = normalized;
        return normalized;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

async function request(url, options = {}, allowRetryOn401 = true) {
  const isWrite =
    options.method && options.method !== "GET" && options.method !== "HEAD";
  const isUpload = options.body instanceof FormData;
  const timeoutMs = isUpload
    ? UPLOAD_TIMEOUT_MS
    : isWrite
    ? DEFAULT_TIMEOUT_MS
    : DEFAULT_TIMEOUT_MS;
  const timeout = withTimeout(options.timeoutMs || timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        Accept: "application/json",
        ...identityHeaders(),
        ...(options.headers || {})
      },
      signal: timeout.signal
    });

    if (response.status === 401 && allowRetryOn401) {
      const refreshed = await tryRefreshAccessToken();
      if (refreshed) return request(url, options, false);
    }
    return response;
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("Request timed out. Please try again.");
    }
    throw error;
  } finally {
    timeout.clear();
  }
}

function toQuery(params) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, String(value));
    }
  }
  return query.toString();
}

async function parseJson(response) {
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const data = await response.json();
      message = data.message || message;
    } catch {
      // ignore
    }
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  if (response.status === 204) return null;
  return response.json();
}

// ─────── HACKATHONS ───────
export const getHackathons = async () => {
  const data = await parseJson(await request(`${API_BASE_URL}/hackathons`));
  return data.items || [];
};
export const getHackathonById = async (id) =>
  parseJson(await request(`${API_BASE_URL}/hackathons/${id}`));
export const createHackathon = async (payload) =>
  parseJson(
    await request(`${API_BASE_URL}/hackathons`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    })
  );
export const updateHackathon = async (id, payload) =>
  parseJson(
    await request(`${API_BASE_URL}/hackathons/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    })
  );
export const deleteHackathon = async (id) =>
  parseJson(await request(`${API_BASE_URL}/hackathons/${id}`, { method: "DELETE" }));

// ─────── DASHBOARD / CALENDAR / INSIGHTS ───────
export const getDashboardStats = async () =>
  parseJson(await request(`${API_BASE_URL}/dashboard/stats`));
export const getDashboardAlerts = async () =>
  parseJson(await request(`${API_BASE_URL}/dashboard/alerts`));
export const getCalendarEvents = async (month, year) =>
  parseJson(await request(`${API_BASE_URL}/calendar/events?${toQuery({ month, year })}`));
export const getInsights = async (range = "6m") =>
  parseJson(await request(`${API_BASE_URL}/insights?${toQuery({ range })}`));
export const exportInsightsJson = async (range = "6m") => {
  const response = await request(`${API_BASE_URL}/insights/export?${toQuery({ range })}`);
  if (!response.ok) throw new Error(`Export failed (${response.status})`);
  return response.blob();
};
export const downloadCalendarIcs = async () => {
  const response = await request(`${API_BASE_URL}/calendar/feed.ics`);
  if (!response.ok) throw new Error(`Calendar export failed (${response.status})`);
  return response.blob();
};

// ─────── REPOSITORIES / IDEAS ───────
export const getRepositories = async () => {
  const data = await parseJson(await request(`${API_BASE_URL}/repositories`));
  return data.items || [];
};
export const createRepository = async (payload) =>
  parseJson(
    await request(`${API_BASE_URL}/repositories`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    })
  );
export const updateRepository = async (id, payload) =>
  parseJson(
    await request(`${API_BASE_URL}/repositories/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    })
  );
export const deleteRepository = async (id) =>
  parseJson(await request(`${API_BASE_URL}/repositories/${id}`, { method: "DELETE" }));

export const getIdeas = async () => {
  const data = await parseJson(await request(`${API_BASE_URL}/ideas`));
  return data.items || [];
};
export const createIdeaEntry = async (payload) =>
  parseJson(
    await request(`${API_BASE_URL}/ideas`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    })
  );
export const deleteIdea = async (id) =>
  parseJson(await request(`${API_BASE_URL}/ideas/${id}`, { method: "DELETE" }));

// ─────── NOTIFICATIONS ───────
export const getNotifications = async ({ limit = 20, unreadOnly = false } = {}) =>
  parseJson(
    await request(
      `${API_BASE_URL}/notifications?${toQuery({
        limit,
        unreadOnly: unreadOnly ? "true" : "false"
      })}`
    )
  );
export const markNotificationAsRead = async (id) =>
  parseJson(await request(`${API_BASE_URL}/notifications/${id}/read`, { method: "PATCH" }));
export const markAllNotificationsAsRead = async () =>
  parseJson(await request(`${API_BASE_URL}/notifications/read-all`, { method: "POST" }));
export const getNotificationPreferences = async () =>
  parseJson(await request(`${API_BASE_URL}/notifications/preferences`));
export const updateNotificationPreferences = async (payload) =>
  parseJson(
    await request(`${API_BASE_URL}/notifications/preferences`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {})
    })
  );
export const syncNotificationQueue = async () =>
  parseJson(await request(`${API_BASE_URL}/notifications/sync`, { method: "POST" }));

// ─────── PROFILE / ACCOUNT ───────
export const getProfile = async () => parseJson(await request(`${API_BASE_URL}/profile`));
export const updateProfile = async (payload) =>
  parseJson(
    await request(`${API_BASE_URL}/profile`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    })
  );
export const deleteAccount = async () =>
  parseJson(await request(`${API_BASE_URL}/account`, { method: "DELETE" }));

// ─────── SHARE / IMPORT ───────
export const listShareLinks = async () => {
  const data = await parseJson(await request(`${API_BASE_URL}/share-links`));
  return data.items || [];
};
export const createShareLink = async (hackathonId, options = {}) =>
  parseJson(
    await request(`${API_BASE_URL}/share-links`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ hackathonId, ...options })
    })
  );
export const revokeShareLink = async (id) =>
  parseJson(await request(`${API_BASE_URL}/share-links/${id}`, { method: "DELETE" }));
export const importShareLink = async (token) =>
  parseJson(
    await request(`${API_BASE_URL}/share-links/import`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token })
    })
  );

// ─────── PARSE (LLM paste-text) ───────
export const getParseStatus = async () =>
  parseJson(await request(`${API_BASE_URL}/parse/status`));
export const parsePastedText = async (text) =>
  parseJson(
    await request(`${API_BASE_URL}/parse/text`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text })
    })
  );

// ─────── PUSH ───────
export const getPushStatus = async () =>
  parseJson(await request(`${API_BASE_URL}/push/status`));
export const registerPushSubscription = async (subscription) =>
  parseJson(
    await request(`${API_BASE_URL}/push/subscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ subscription })
    })
  );
export const unregisterPushSubscription = async (endpoint) =>
  parseJson(
    await request(`${API_BASE_URL}/push/unsubscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ endpoint })
    })
  );

// ─────── TEAM MEMBERS ───────
export const generateInviteCode = async (hackathonId) =>
  parseJson(
    await request(`${API_BASE_URL}/hackathons/${hackathonId}/invite-code`, {
      method: "POST"
    })
  );
export const revokeInviteCode = async (hackathonId) =>
  parseJson(
    await request(`${API_BASE_URL}/hackathons/${hackathonId}/invite-code`, {
      method: "DELETE"
    })
  );
export const joinByInviteCode = async (code) =>
  parseJson(
    await request(`${API_BASE_URL}/hackathons/join`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code })
    })
  );
export const listMembers = async (hackathonId) => {
  const data = await parseJson(
    await request(`${API_BASE_URL}/hackathons/${hackathonId}/members`)
  );
  return data.members || [];
};
export const removeMember = async (hackathonId, userRef) =>
  parseJson(
    await request(`${API_BASE_URL}/hackathons/${hackathonId}/members/${userRef}`, {
      method: "DELETE"
    })
  );
